import { createContext } from "react";
import { logger } from "@util/logger.util";

// ~-~-~-~-~-~-~-
// Context
// ~-~-~-~-~-~-~-
export const WebSettingsContext = createContext({ settingsViewId: "" });

// ~-~-~-~-~-~-~-
// View
// ~-~-~-~-~-~-~-
export enum WebSettingsSections {
  Account = "Account",
  Teams = "Teams",
  Organization = "Organization",
  Internal = "Internal",
}

export enum AccountSections {
  HoursAndPreferences = "HoursAndPreferences",
  SmartHolds = "SmartHolds",
  Integrations = "Integrations",
  EventColorCoding = "EventColorCoding",
  TeamAvailabilityCalendar = "TeamAvailabilityCalendar",
  TeamNoMeetingDay = "TeamNoMeetingDay",
  EmailsAndNotifications = "EmailsAndNotifications",
}

export enum HoursAndPreferencesSections {
  WorkingHours = "WorkingHours",
  MeetingHours = "MeetingHours",
  TimeZone = "TimeZone",
  MeetingBreak = "MeetingBreak",
  TimeOfDayPreference = "TimeOfDayPreference",
  PreferredMeetingRooms = "PreferredMeetingRooms",
}

export enum SmartHoldsSections {
  FocusTime = "FocusTime",
  LunchHold = "LunchHold",
  TravelTime = "TravelTime",
  PersonalCalendarHold = "PersonalCalendarHold",
}

export enum IntegrationsSections {
  Slack = "Slack",
  Zoom = "Zoom",
  Asana = "Asana",
}

export enum TeamsSections {
  CreateOrJoinTeam = "CreateOrJoinTeam",
}

export enum OrganizationSections {
  Analytics = "Analytics",
  Members = "Members",
  Teams = "Teams",
  PlansAndBilling = "PlansAndBilling",
  OrganizationSettings = "OrganizationSettings",
}

export enum InternalSections {
  Experiments = "Experiments",
}

// ~-~-~-~-~-~-~-
// Routes
// ~-~-~-~-~-~-~-
const getEnumForPath = (path: string) => {
  switch (path) {
    // web-settings sections
    case "account":
      return WebSettingsSections.Account;
    case "teams":
      return WebSettingsSections.Teams;
    case "organization":
      return WebSettingsSections.Organization;
    case "internal":
      return WebSettingsSections.Internal;
    // account sections
    case "hours-and-preferences":
      return AccountSections.HoursAndPreferences;
    case "smart-holds":
      return AccountSections.SmartHolds;
    case "integrations":
      return AccountSections.Integrations;
    case "event-color-coding":
      return AccountSections.EventColorCoding;
    case "team-availability-calendar":
      return AccountSections.TeamAvailabilityCalendar;
    case "team-no-meeting-day":
      return AccountSections.TeamNoMeetingDay;
    case "emails-and-notifications":
      return AccountSections.EmailsAndNotifications;
    // teams sections
    case "create-or-join-team":
      return TeamsSections.CreateOrJoinTeam;
    // organization sections
    case "analytics":
      return OrganizationSections.Analytics;
    case "members":
      return OrganizationSections.Members;
    case "teams":
      return OrganizationSections.Teams;
    case "plans-and-billing":
      return OrganizationSections.PlansAndBilling;
    case "organization-settings":
      return OrganizationSections.OrganizationSettings;
    // internal sections
    case "experiments":
      return InternalSections.Experiments;
    // hours-and-preferences sections
    case "working-hours":
      return HoursAndPreferencesSections.WorkingHours;
    case "meeting-hours":
      return HoursAndPreferencesSections.MeetingHours;
    case "time-zone":
      return HoursAndPreferencesSections.TimeZone;
    case "meeting-break":
      return HoursAndPreferencesSections.MeetingBreak;
    case "time-of-day-preference":
      return HoursAndPreferencesSections.TimeOfDayPreference;
    case "preferred-meeting-rooms":
      return HoursAndPreferencesSections.PreferredMeetingRooms;
    // smart-hold sections
    case "focus-time":
      return SmartHoldsSections.FocusTime;
    case "lunch-hold":
      return SmartHoldsSections.LunchHold;
    case "travel-time":
      return SmartHoldsSections.TravelTime;
    case "personal-calendar-hold":
      return SmartHoldsSections.PersonalCalendarHold;
    // integrations sections
    case "slack":
      return IntegrationsSections.Slack;
    case "zoom":
      return IntegrationsSections.Zoom;
    case "asana":
      return IntegrationsSections.Asana;
    default:
      logger.error("failed to getEnumForPath in web settings", path);
      return "";
  }
};

export const getSettingsViewIdForRoute = (windowLocation: Location) => {
  const paths = windowLocation.pathname.split("/web-settings")[1].split("/").filter(Boolean);
  return paths.map(getEnumForPath).join("-");
};

// ~-~-~-~-~-~-~-
// Settings View Id
// ~-~-~-~-~-~-~-
export const getExpandableTreeItemFromViewId = (settingsViewId: string) => {
  return settingsViewId.split("-").slice(0, 2).join("-");
};

export const createSettingsViewId = (
  section: string,
  subSection?: string,
  subSubSection?: string,
) => {
  return [section, subSection, subSubSection].filter(Boolean).join("-");
};

// ~-~-~-~-~-~-~-
// Scroll helpers
// ~-~-~-~-~-~-~-
const getScrollKeyFromViewId = (settingsViewId: string) => {
  return settingsViewId.split("-").slice(-1).join("-");
};

const getWebSettingsElement = (key: string) => {
  return document.querySelector(`[cw-id="web-settings-${key}"]`);
};

const getWebSettingsElementScrollOffset = (key: string) => {
  const el = getWebSettingsElement(key);
  return el ? (el as HTMLElement).offsetTop - 100 : 0;
};

const setWebSettingsContainerScrollTop = (scrollTop: number) => {
  const el = document.querySelector('[cw-id="web-settings-container"]') as Element;
  el.scrollTop = scrollTop;
};

export const scroll = (settingsViewId: string) => {
  const scrollTop = getWebSettingsElementScrollOffset(getScrollKeyFromViewId(settingsViewId));
  setWebSettingsContainerScrollTop(scrollTop);
};
